import React from 'react';
import InfoTip from '../../shared/InfoTip';
import { fmtCurrency } from '../../shared/format';
import { Skeleton } from '../../shared/Skeleton';
import { ErrorState } from '../../shared/StateMessage';

const STATUS_COLORS = {
  on_track: { bar: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', label: 'On Track', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
  underpacing: { bar: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400', label: 'Underpacing', bg: 'bg-amber-50 dark:bg-amber-900/20' },
  overpacing: { bar: 'bg-red-500', text: 'text-red-600 dark:text-red-400', label: 'Overpacing', bg: 'bg-red-50 dark:bg-red-900/20' },
  no_budget: { bar: 'bg-gray-400', text: 'text-gray-500 dark:text-gray-400', label: 'No Budget', bg: 'bg-gray-50 dark:bg-gray-800' },
};

const BudgetPacing = ({ budgetPacing, loading, error, referenceDate }) => {
  // Label the month from the same date the pacing is anchored to (latest data date),
  // falling back to the API's own last_data_date, then today. Never the picker's range.
  const labelDate = referenceDate || budgetPacing?.last_data_date;
  const monthLabel = (labelDate ? new Date(labelDate + 'T00:00:00') : new Date())
    .toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold inline-flex items-center">
          Budget Pacing
          <InfoTip text="Where this month's spend sits against your monthly budget, plus a projection of where you'll land if the current pace holds." />
        </div>
        <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
          Budget pacing for the month of {monthLabel}
        </span>
      </div>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 sm:p-6">
        {error ? (
          <ErrorState message={error} />
        ) : loading ? (
          <Skeleton className="h-8 rounded-full" />
        ) : !budgetPacing || budgetPacing.monthly_budget === 0 ? (
          <div>
            <div className="relative h-8 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Budget pacing unavailable — no daily budget configured in campaign settings.
                </span>
              </div>
            </div>
          </div>
        ) : (
          (() => {
            const bp = budgetPacing;
            const status = STATUS_COLORS[bp.pacing_status] || STATUS_COLORS.no_budget;
            const usedPct = Math.min(bp.budget_used_pct, 100);
            const elapsedPct = Math.min(bp.month_elapsed_pct, 100);

            return (
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-10">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${status.bg} ${status.text}`}>
                      <span className={`w-2 h-2 rounded-full ${status.bar}`}></span>
                      {status.label}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {bp.active_campaigns} active campaign{bp.active_campaigns !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                    {bp.budget_used_pct}% of budget used
                  </span>
                </div>

                <div className="relative h-8 bg-gray-100 dark:bg-gray-800 rounded-full overflow-visible">
                  <div
                    className={`absolute top-0 left-0 h-full rounded-full ${status.bar} transition-all duration-700 ease-out`}
                    style={{ width: `${usedPct}%`, minWidth: usedPct > 0 ? '8px' : '0' }}
                  />
                  <div
                    className="absolute top-0 h-full flex flex-col items-center z-10"
                    style={{ left: `${elapsedPct}%` }}
                  >
                    {elapsedPct < 100 && (
                      <div className="w-0.5 h-full bg-gray-900 dark:bg-white opacity-60"></div>
                    )}
                    <div className="absolute -top-9 -translate-x-1/2 whitespace-nowrap">
                      <span className="text-[12px] font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 px-1.5 py-0.5 rounded shadow-sm border dark:border-gray-700">
                        {bp.month_elapsed_pct}% of month
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-700 dark:text-gray-300">
                  <span>
                    <span className="font-semibold">{fmtCurrency(bp.mtd_cost)}</span>
                    <span className="text-gray-500 dark:text-gray-400"> of ~{fmtCurrency(bp.monthly_budget)} spent</span>
                  </span>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <span>
                    Projected: <span className="font-semibold">{fmtCurrency(bp.projected_monthly_spend)}</span>
                  </span>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <span>
                    <span className="font-semibold">{bp.days_remaining}</span> days remaining
                  </span>
                </div>

                <p className="mt-2 text-[13px] text-gray-400 dark:text-gray-500 leading-relaxed">
                  Budget based on active campaign daily budgets × 30.4. Google may spend up to 2× daily budget on high-traffic days.
                </p>
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
};

export default BudgetPacing;
